import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  AlignItemsProps,
  AlignSelfProps,
  BackgroundColorProps,
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  alignSelf,
  backgroundColor,
  borderRadius,
  boxShadow,
  display,
  space,
  width,
} from 'styled-system';

import { Key } from 'common/enums/Keyboard';
import { useButton3D } from 'common/hooks/useButton3D';
import type {
  BoxSizingProps,
  FlexGapProps,
  FlexShrinkProps,
  ThemeHeightProps,
} from 'common/theme/system-utilities';
import { boxSizing, flexGap, flexShrink, themeHeight } from 'common/theme/system-utilities';
import { breakpoints } from 'common/theme/theme';
import type { GalleryTrackingProps } from 'common/tracking/productGallery';
import { GalleryTabMenu3DButton } from './GalleryTabMenu3DButton';
import { TabItem } from './TabItem';

export interface GalleryTabMenuTab {
  id: string;
  label: string;
}

export const GALLERY_3D_TAB_ID = '3d';

export interface GalleryTabMenuProps {
  tabs: GalleryTabMenuTab[];
  activeTabId?: string;
  onTabSelect: (id: string) => void;
  model3DData?: Nullable<{ path: string }>;
  tracking?: GalleryTrackingProps;
}

const TabMenuContainer = styled.div<
  DisplayProps &
    AlignItemsProps &
    BorderRadiusProps &
    BackgroundColorProps &
    ThemeHeightProps &
    SpaceProps &
    BoxSizingProps &
    FlexGapProps &
    BoxShadowProps & { showTabs: boolean }
>`
  ${display};
  ${alignItems};
  ${borderRadius};
  ${backgroundColor};
  ${themeHeight};
  ${space};
  ${boxSizing};
  ${flexGap};
  ${boxShadow};

  > :only-child {
    flex: 1;
  }

  ${({ showTabs }) =>
    showTabs
      ? `
    width: 100%;
    max-width: 360px;
    @media (min-width: ${breakpoints[2]}) {
      max-width: 400px;
    }
    `
      : `
    width: 70px;
    padding: 0;
  `}
`;

const tabMenuContainerStyles = {
  display: 'inline-flex',
  alignItems: 'stretch',
  themeHeight: ['sp_40', null, null, 'sp_48'],
  backgroundColor: 'sys.neutral.background.default',
  borderRadius: '100px',
  boxSizing: 'border-box' as BoxSizingProps['boxSizing'],
  boxShadow: 'low',
  p: 'sp_4',
  gap: ['sp_4', null, null, 'sp_2'],
};

const Separator = styled.div<WidthProps & FlexShrinkProps & AlignSelfProps & BackgroundColorProps>`
  ${width};
  ${flexShrink};
  ${alignSelf};
  ${backgroundColor};
`;

export const GalleryTabMenu = ({
  tabs,
  activeTabId,
  onTabSelect,
  model3DData = null,
  tracking,
}: GalleryTabMenuProps) => {
  const activeIndex = tabs.findIndex(tab => tab.id === activeTabId);
  const [focusedIndex, setFocusedIndex] = useState(activeIndex >= 0 ? activeIndex : 0);
  const tabRefs = useRef<Array<Nullable<HTMLButtonElement>>>([]);
  const { onClick: on3DClick } = useButton3D(model3DData, tracking);

  useEffect(() => {
    const newIndex = tabs.findIndex(tab => tab.id === activeTabId);
    if (newIndex >= 0) setFocusedIndex(newIndex);
  }, [activeTabId, tabs]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const lastIndex = tabs.length - 1;
    let nextIndex: Nullable<number> = null;

    switch (event.key) {
      case Key.ArrowRight:
        nextIndex = focusedIndex < lastIndex ? focusedIndex + 1 : 0;
        break;
      case Key.ArrowLeft:
        nextIndex = focusedIndex > 0 ? focusedIndex - 1 : lastIndex;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    setFocusedIndex(nextIndex);
    tabRefs.current[nextIndex]?.focus();
  };

  const showTabs = tabs.length > 1;
  const has3DView = Boolean(model3DData);

  if (!showTabs && !has3DView) return null;

  return (
    <TabMenuContainer
      {...tabMenuContainerStyles}
      showTabs={showTabs}
      role="tablist"
      onKeyDown={handleKeyDown}
    >
      {showTabs &&
        tabs.map((tab, index) => (
          <TabItem
            key={tab.id}
            id={tab.id}
            label={tab.label}
            isActive={tab.id === activeTabId}
            index={index}
            count={tabs.length}
            tabIndex={index === focusedIndex ? 0 : -1}
            onClick={() => {
              setFocusedIndex(index);
              onTabSelect(tab.id);
            }}
            buttonRef={(el: Nullable<HTMLButtonElement>) => {
              tabRefs.current[index] = el;
            }}
          />
        ))}
      {has3DView && (
        <>
          {showTabs && (
            <Separator
              width="1px"
              flexShrink={0}
              alignSelf="stretch"
              backgroundColor="sys.neutral.border.weak"
            />
          )}
          <GalleryTabMenu3DButton
            isActive={activeTabId === GALLERY_3D_TAB_ID}
            onClick={on3DClick}
          />
        </>
      )}
    </TabMenuContainer>
  );
};

GalleryTabMenu.displayName = 'GalleryTabMenu';

export default GalleryTabMenu;
